/**
 * AuthContext.tsx
 *
 * Sign-in strategy: signInWithPopup on ALL platforms (desktop + mobile).
 *
 * Why not signInWithRedirect on mobile:
 *   Firebase's redirect flow relies on cross-origin cookies and localStorage
 *   persistence across a domain hop. Both are blocked by default in:
 *     - Safari / iOS 16.4+ (ITP + storage partitioning)
 *     - Chrome Android with third-party cookie restrictions
 *   The result is getRedirectResult() returning null silently, leaving
 *   loading: true forever. This is a Firebase SDK documented limitation,
 *   not a fixable code issue on our end.
 *
 * Why signInWithPopup works on mobile:
 *   A direct user tap (the sign-in button) counts as a user gesture, which
 *   grants popup permission in both Chrome Android and Safari iOS. The popup
 *   opens Google's OAuth page in a small window, the user authenticates, the
 *   window closes, and onAuthStateChanged fires — no cross-origin cookie needed.
 *
 * getRedirectResult() is still called once on mount as a safety net for any
 * user who was mid-redirect from a previous session before this change.
 */
import React, { createContext, useContext, ReactNode, useEffect, useState } from "react";
import {
  GoogleAuthProvider,
  signOut,
	onAuthStateChanged,
	signInWithRedirect,
  signInWithPopup,
  getRedirectResult,
} from "firebase/auth";
import { auth } from "../firebase";
import User from "../models/UserModel";

type AuthContextType = {
  user:         User | null;
  loading:      boolean;
  googleSignIn: () => Promise<void>;
  logOut:       () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const [user,    setUser]    = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const init = async () => {
      // ── Safety net + Mobile Redirect Fallback Catch ──
      try {
        const result = await getRedirectResult(auth);
        if (result) {
          const credential = GoogleAuthProvider.credentialFromResult(result);
          if (credential?.accessToken) {
            localStorage.setItem("accessToken", credential.accessToken);
          }
        }
      } catch (error) {
         console.error("[Auth] Redirect result error:", error);
      }

      // ── Start auth state listener ──
      unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
        if (firebaseUser) {
          const { displayName, email, uid, photoURL, metadata } = firebaseUser;
          setUser({
            id:          uid,
            name:        displayName || "",
            email:       email       || "",
            photoURL:    photoURL    || "",
            createdAt:   new Date(metadata.creationTime  || ""),
            lastLogin:   new Date(metadata.lastSignInTime || ""),
            homeAddress: "",
            workAddress: "",
            birthday:    null,
            gender:      "",
          });
        } else {
          setUser(null);
        }
        setLoading(false);
      });
    };

    init();
    return () => { if (unsubscribe) unsubscribe(); };
  }, []);

  const googleSignIn = async () => {
    const provider = new GoogleAuthProvider();

    try {
      setLoading(true);
      const result = await signInWithPopup(auth, provider);
      const credential = GoogleAuthProvider.credentialFromResult(result);
      if (credential?.accessToken) {
        localStorage.setItem("accessToken", credential.accessToken);
      }
    } catch (error: any) {
      const code = error?.code ?? "";

      // ── THE FIX: Fallback to redirect if mobile blocks the popup ──
      if (code === "auth/popup-blocked") {
        console.warn("[Auth] Popup blocked. Falling back to redirect...");
        await signInWithRedirect(auth, provider);
        return; // Halt execution here, the page will unload and redirect
      }

      if (
        code === "auth/popup-closed-by-user" ||
        code === "auth/cancelled-popup-request"
      ) {
        console.info(`[Auth] Popup dismissed (${code}) — user can try again.`);
      } else {
        console.error("[Auth] Sign-in error:", code, error?.message);
      }

      setLoading(false);
      // CRITICAL: Throw the error so SignInPage knows the attempt failed
      throw error;
    }
  };

  const logOut = async () => {
    setLoading(true);
    try {
      localStorage.removeItem("accessToken");
      await signOut(auth);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, googleSignIn, logOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const UserAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("UserAuth must be used within AuthContextProvider");
  return context;
};
